import { useMemo, useSyncExternalStore } from 'react';
import { useAudio } from './useAudio';
import {
  getSpeakingBusy,
  getSpeakingKey,
  getSpeakingWord,
  isSpeakingSession,
  setSpeakingBusy,
  setSpeakingKey,
  setSpeakingWord,
  startSpeakingSession,
  subscribeSpeaking,
} from '../lib/speaking';
import { estimateWordTimes } from '../lib/alignWords';
import { playbackEpoch, preloadSounds } from '../lib/audio';
import { lockGame } from '../lib/gameLock';

/** Rango de caracteres de una palabra dentro del texto de un `SayStep`. */
export type WordRange = { start: number; end: number };

export type SayStep = {
  /** Texto a leer. Siempre va, aunque haya grabación: es lo que se lee si la
   *  grabación no está, y de su largo sale la red de seguridad. */
  text: string;
  /** Grabación propia de ese texto. Si está, se usa en vez del TTS. */
  src?: string;
  /** Identidad de lo que suena, para que el componente sepa qué resaltar
   *  (ver `useSpeakingKey`). Sin key igual se lee, sin resaltar nada. */
  key?: string | null;
  lang?: string;
  rate?: number;
  pitch?: number;
  /** Rangos de caracteres de las palabras de `text`, en orden. Con esto la
   *  lectura publica en qué palabra va (ver `useSpeakingWord`) y el karaoke de
   *  los cuentos resalta sin llevar su propio cronómetro. Con grabación, el
   *  avance sale de `wordTimes`, o de una estimación por el largo de cada
   *  palabra si no hay. */
  words?: WordRange[];
  /** Segundo en que empieza cada palabra de `words` dentro de `src` (ver
   *  `alignWordTimes`). Sin esto, una grabación con `words` se estima. */
  wordTimes?: number[] | null;
};

type SayOptions = {
  /** Espera antes de la primera lectura (deja asentar una transición). */
  startDelay?: number;
  /** Pausa entre lecturas de una secuencia. */
  gap?: number;
  /** Al terminar toda la secuencia (no se llama si la cancelan). */
  onDone?: () => void;
};

/** Cancela una lectura en curso. Idempotente. */
export type Cancel = () => void;

const DEFAULT_GAP = 350;

/**
 * Cuánto esperar como mucho a que termine una lectura TTS antes de seguir de
 * largo. El `onend` de speechSynthesis en iOS a veces no llega nunca, y sin
 * tope una secuencia se quedaría trabada para siempre en la primera palabra.
 *
 * Proporcional al texto y sin techo corto: tenía un máximo de 6s, y una página
 * de cuento tarda 10–15s en leerse. A los 6s se daba por terminada con la voz
 * todavía leyendo, y el resaltado del karaoke se apagaba a mitad de la página.
 */
function safetyMs(text: string): number {
  return Math.min(120000, Math.max(1500, 400 + text.length * 120));
}

/**
 * Cuánto tarda el TTS en decir una palabra, para el karaoke cuando
 * `onboundary` no llega (iOS no lo emite nunca). Por letras y no fijo: con
 * 450ms por palabra, "a" y "zoológico" duraban lo mismo y el resaltado se
 * adelantaba o se atrasaba. Una coma o un punto después suma la pausa que
 * hace la voz. ~68ms por letra es el ritmo del TTS en español a rate 1.
 */
function wordMs(word: string, after: string, rate: number): number {
  const pause = /[.!?¡¿:;]/.test(after) ? 380 : /,/.test(after) ? 200 : 0;
  return ((word.length + 1) * 68) / rate + pause;
}

/** Tope para que una grabación empiece a sonar. Después, mientras avance
 *  (evento `timeupdate`), el tope se renueva solo: no depende de que el
 *  navegador informe la duración, que en iOS a veces no llega y dejaba el
 *  tope de arranque cortando la página a los 10s. */
const SRC_START_MS = 10000;
const SRC_STALL_MS = 4000;

/** Cada cuánto se mira por dónde va una grabación para mover el karaoke. */
const SRC_POLL_MS = 80;

/**
 * Leer en voz alta, resaltando lo que suena.
 *
 * Unifica el patrón que estaba copiado en media docena de vistas — "grabación
 * propia si existe, TTS si no" — y le suma lo que cada una resolvía por su
 * cuenta: publicar qué se está leyendo (para el resaltado, ver
 * `useSpeakingKey` y la clase `.is-speaking`), cancelar al interrumpir, y no
 * quedarse colgado si el `onend` no llega.
 *
 * ```tsx
 * const { say, sayEach, stop } = useSay();
 * const speakingKey = useSpeakingKey();
 *
 * say({ key: item.word, text: item.word, src: transcriptionSrc(item) });
 * <div className={speakingKey === item.word ? 'is-speaking' : ''}>…</div>
 * ```
 */
export function useSay() {
  const audio = useAudio();

  return useMemo(() => {
    /** Lee un paso y avisa cuando termina (por `onEnd`, por error o por tope). */
    const runStep = (step: SayStep, session: number, onStepDone: () => void) => {
      if (!isSpeakingSession(session)) return;
      setSpeakingKey(step.key ?? null);
      // Dos pasos seguidos con la misma key no disparan el reset de adentro de
      // setSpeakingKey, así que la palabra del paso anterior se limpia acá.
      if (!step.words?.length) setSpeakingWord(null);

      let wordTimer: number | null = null;
      const stopWordTimer = () => {
        if (wordTimer !== null) {
          // Interval en grabaciones, timeout encadenado en TTS: el id es del
          // mismo espacio y clearTimeout/clearInterval se aceptan cruzados,
          // pero se llama a los dos para no depender de eso.
          window.clearInterval(wordTimer);
          window.clearTimeout(wordTimer);
          wordTimer = null;
        }
      };

      let finished = false;
      let safety = 0;
      /** El canal de audio al empezar este paso (ver `playbackEpoch`). */
      let myEpoch = -1;
      const done = () => {
        stopWordTimer();
        window.clearTimeout(safety);
        if (finished || !isSpeakingSession(session)) return;
        finished = true;
        // Otro sonido tomó el canal (un efecto, un preview, un video): esta
        // lectura no terminó, la cortaron. Se termina la secuencia entera en
        // vez de seguir con el paso siguiente, que cortaría a ese sonido.
        if (myEpoch !== -1 && playbackEpoch() !== myEpoch) {
          startSpeakingSession();
          setSpeakingKey(null);
          setSpeakingWord(null);
          setSpeakingBusy(false);
          return;
        }
        onStepDone();
      };

      safety = window.setTimeout(done, step.src ? SRC_START_MS : safetyMs(step.text));

      if (step.src) {
        // playSound puede llamar onError dos veces (evento 'error' y el catch
        // de play()); el guard `finished` lo absorbe.
        const el = audio.play(step.src, { onEnd: done, onError: done });
        if (!el) { done(); return; }
        myEpoch = playbackEpoch();
        el.addEventListener('timeupdate', () => {
          if (finished) return;
          window.clearTimeout(safety);
          safety = window.setTimeout(done, SRC_STALL_MS);
        });

        const ranges = step.words;
        if (ranges && ranges.length > 0) {
          setSpeakingWord(0);
          const texts = ranges.map((r) => step.text.slice(r.start, r.end));
          // Sin tiempos de Whisper: repartir la duración por el largo de cada
          // palabra. Y si el navegador no informa la duración (iOS, a veces),
          // el mismo ritmo por letras del TTS: sin esto el resaltado quedaba
          // clavado en la primera palabra toda la página.
          let paced: number[] | null = null;
          const pacedTimes = () => {
            if (!paced) {
              let acc = 0;
              paced = ranges.map((r, i) => {
                const at = acc / 1000;
                acc += wordMs(texts[i], step.text.slice(r.end, ranges[i + 1]?.start ?? step.text.length), 0.9);
                return at;
              });
            }
            return paced;
          };
          let times = step.wordTimes ?? null;
          let current = 0;
          wordTimer = window.setInterval(() => {
            if (!isSpeakingSession(session)) { stopWordTimer(); return; }
            if (!times && Number.isFinite(el.duration)) times = estimateWordTimes(texts, el.duration);
            const t = el.currentTime;
            let i = current;
            const tt = times ?? pacedTimes();
            while (i + 1 < tt.length && tt[i + 1] <= t) i++;
            if (i !== current) { current = i; setSpeakingWord(i); }
          }, SRC_POLL_MS);
        }
        return;
      }

      const words = step.words;
      let gotBoundary = false;

      if (words && words.length > 0) {
        setSpeakingWord(0);
        const rate = step.rate ?? 0.9;
        let i = 0;
        const next = () => {
          // Se apaga sola si la cancelaron o si el navegador sí emite
          // boundary: ahí manda el dato real y no la estimación.
          if (!isSpeakingSession(session) || gotBoundary || finished) return;
          i++;
          if (i >= words.length) return;
          setSpeakingWord(i);
          schedule();
        };
        const schedule = () => {
          const w = words[i];
          const after = step.text.slice(w.end, words[i + 1]?.start ?? step.text.length);
          wordTimer = window.setTimeout(next, wordMs(step.text.slice(w.start, w.end), after, rate));
        };
        schedule();
      }

      audio.speak(step.text, {
        /* Solo las que el paso define de verdad. `useAudio` arma sus opciones
           con `{ lang: config.tts_lang, …, ...overrides }`, así que mandar
           `lang: undefined` NO es "no opinar": pisa la config del admin y
           `speak()` termina cayendo a su default es-AR / rate 0.9. Con la voz
           en español no se nota, pero el rate y el pitch de AdminConfig se
           perdían en toda lectura que no los pasara explícitos. */
        ...(step.lang !== undefined && { lang: step.lang }),
        ...(step.rate !== undefined && { rate: step.rate }),
        ...(step.pitch !== undefined && { pitch: step.pitch }),
        onBoundary: words && words.length > 0
          ? (charIndex) => {
              if (!isSpeakingSession(session)) return;
              gotBoundary = true;
              stopWordTimer();
              let i = words.findIndex((w) => charIndex >= w.start && charIndex < w.end);
              // Si el índice cae entre dos palabras (un espacio, un signo),
              // adelantar a la siguiente en vez de apagar el resaltado.
              if (i === -1) i = words.findIndex((w) => w.start >= charIndex);
              if (i !== -1) setSpeakingWord(i);
            }
          : undefined,
        onEnd: done,
      });
      myEpoch = playbackEpoch();
    };

    const sayEach = (steps: SayStep[], opts: SayOptions = {}): Cancel => {
      const session = startSpeakingSession();
      const cancel: Cancel = () => {
        if (!isSpeakingSession(session)) return;
        startSpeakingSession();
        setSpeakingKey(null);
        setSpeakingWord(null);
        setSpeakingBusy(false);
      };
      if (steps.length === 0) return cancel;
      // Los pasos 2..n se cargan mientras suena el primero: sin esto cada
      // grabación de la secuencia arrancaba con su propia demora.
      preloadSounds(steps.slice(1).map((s) => s.src));
      // De principio a fin de la secuencia, gaps entre pasos incluidos — ver
      // el comentario de `busy` en lib/speaking.ts.
      setSpeakingBusy(true);
      // Friza el juego (si hay uno montado — ver lib/gameLock.ts) mientras
      // dura esta lectura, sea la que sea: una letra tecleada en Escribir, un
      // dígito en Teléfono, la explicación de un error. "La única manera de
      // cortar un sonido es salir de la sección" — useNav.ts es quien de
      // verdad corta algo al navegar; acá solo se evita que OTRO toque en el
      // mismo juego le gane el canal de audio a la palabra en curso.
      lockGame({ minMs: 300, maxMs: 12000 });

      const { gap = DEFAULT_GAP, startDelay = 0, onDone } = opts;

      const runAt = (i: number) => {
        if (!isSpeakingSession(session)) return;
        if (i >= steps.length) {
          setSpeakingKey(null);
          setSpeakingBusy(false);
          onDone?.();
          return;
        }
        runStep(steps[i], session, () => {
          if (i + 1 >= steps.length) {
            // Última: se apaga el resaltado sin esperar el gap.
            setSpeakingKey(null);
            setSpeakingBusy(false);
            onDone?.();
            return;
          }
          window.setTimeout(() => runAt(i + 1), gap);
        });
      };

      if (startDelay > 0) window.setTimeout(() => runAt(0), startDelay);
      else runAt(0);

      return cancel;
    };

    const say = (step: SayStep, onDone?: () => void): Cancel =>
      sayEach([step], { onDone });

    /** Corta lo que esté sonando y apaga el resaltado. */
    const stop = () => {
      startSpeakingSession();
      setSpeakingKey(null);
      setSpeakingWord(null);
      setSpeakingBusy(false);
      audio.stop();
    };

    return { say, sayEach, stop };
  }, [audio]);
}

/** Key de lo que se está leyendo ahora, o null. Ver `.is-speaking`. */
export function useSpeakingKey(): string | null {
  return useSyncExternalStore(subscribeSpeaking, getSpeakingKey, getSpeakingKey);
}

/** Hay una lectura en curso, de principio a fin, con key o sin ella (ver
 *  `busy` en lib/speaking.ts). Es lo que mueve la boca de las mascotas (`<SectionMascot>`). */
export function useSpeakingBusy(): boolean {
  return useSyncExternalStore(subscribeSpeaking, getSpeakingBusy, getSpeakingBusy);
}

/**
 * Índice de la palabra que suena, para las lecturas que pasaron `words`.
 * Mirala siempre junto con la key —`speakingKey === miKey && speakingWord === i`—
 * porque el índice es global y puede haber varias páginas montadas.
 */
export function useSpeakingWord(): number | null {
  return useSyncExternalStore(subscribeSpeaking, getSpeakingWord, getSpeakingWord);
}
